import { useCallback, useState } from "react";
import { useGetNewReportMutation, useEnsureAccountHistoryForReportMutation } from "features/trading/accounts";
import { NewReportRequest, NewReportResponse } from "helpers/types";
import { useSettingsReportValue } from "hooks/useReportStorage";
import { AUTO_GENERATE_HISTORY_SETTING_KEY } from "constants/index";
import { extractReportErrorMessage } from "./extractReportErrorMessage";
import { AlertColor } from "@mui/material";

type UseReportCalculationOptions = {
  fallbackErrorMessage: string;
};

type ReportData = NewReportResponse["data"];

type HistoryNotice = {
  status: AlertColor;
  message: string;
};

export const useReportCalculation = ({
  fallbackErrorMessage,
}: UseReportCalculationOptions) => {
  const [getNewReport, { isLoading: isCalculating }] =
    useGetNewReportMutation();
  const [ensureHistory, { isLoading: isEnsuringHistory }] =
    useEnsureAccountHistoryForReportMutation();
  const [autoGenerateHistory] = useSettingsReportValue(
    AUTO_GENERATE_HISTORY_SETTING_KEY,
    "true",
  );
  const [error, setError] = useState("");
  const [historyNotice, setHistoryNotice] = useState<HistoryNotice | null>(
    null,
  );

  const calculate = useCallback(
    async (args: NewReportRequest): Promise<ReportData | null> => {
      setHistoryNotice(null);

      // when enabled, create account history first
      if (autoGenerateHistory !== "false") {
        try {
          const { data: created } = await ensureHistory({
            orion_account_id: args.orion_account_id,
            strategy_id: args.strategy_id,
          }).unwrap();
          if (created.length > 0) {
            setHistoryNotice({
              status: "success",
              message: "Account history was created successfully.",
            });
          }
        } catch (e) {
          setHistoryNotice({
            status: "error",
            message: extractReportErrorMessage(
              e,
              "Couldn't create account history. Please check the settings.",
            ),
          });
          return null;
        }
      }

      try {
        const response = await getNewReport(args).unwrap();
        return response.data;
      } catch (e) {
        setError(extractReportErrorMessage(e, fallbackErrorMessage));
        return null;
      }
    },
    [getNewReport, ensureHistory, autoGenerateHistory, fallbackErrorMessage],
  );

  return {
    calculate,
    error,
    setError,
    historyNotice,
    setHistoryNotice,
    isCalculating,
    isEnsuringHistory,
  };
};
